// Users (Team members) — adopts the SAME app-surface chrome as /calendar
// (per Nika 2026-06-22 "make users and claims same layout (sidebar and layout)
// as on calendar page"): AppPage row → an AppSidebar-extending rail (free
// ViewSwitcher card + the team / members filter cards) + an AppMain-extending
// column with a PageHeader (title) and the people grid in its Body.
//
// The filter content (PpTeamsRail / PpMembersSummary) and the body grid
// (PpGroups / PpEmpty) are the same DB-sourced lists as before — only the
// chrome changed so this surface matches Calendar's sidebar + card layout.

// Header button style — matches pages/data-external.js's _headerBtn (the
// house neutral header-action convention: no border, `overlay` fill,
// `hover` wash, never a one-off accent).
const _headerBtn = {
  tag: 'button',
  type: 'button',
  minHeight: 'controlH',
  padding: '0 A',
  round: 'radiusPill',
  border: 'none',
  background: 'overlay',
  color: 'title',
  fontSize: 'Z2',
  fontWeight: '700',
  cursor: 'pointer',
  ':hover': { background: 'hover' }
}

export const people = {
  extends: 'AppPage',
  metadata: (el) => ({ title: el.call('pageTitle', 'workspace.people.page.title') }),

  // Org chart — bulk manager editor + minimal chart view (tickets/sonnet.md
  // "org chart: UI"). Toggled via root._orgChartOpen; see
  // components/OrgChartModal.js + functions/orgChart.js.
  OrgChartModal: {},

  AppSidebar: {
    tag: 'nav',
    'aria-label': (el) => el.call('polyglot', 'workspace.people.navAria'),
    // On the page-sidebar rule (shared functions/pageSidebar.js): opposite a
    // rail navbar, hideable per page. The first card opens with its caption, so
    // the hide button's corner is free.
    pageSidebar: true,

    // Drop the inherited VIEWS switcher (Frontend/Infra/Tasks/Agents) — /team-members
    // is not a canvas view. (2026-06-22 per Nika "remove Views from /team-members".)
    ViewSwitcher: null,

    // Teams rail — DB-sourced (peopleTeamsDirectory: Mongo Team/TeamMember via
    // the SDK), one filter link per team with its membership count, split by
    // team category: Active = manager/active, Inactive = passive/external/
    // archived. Its own AppSidebar card (CommonCard via childExtends).
    PpTeamsRail: {
      flow: 'y',
      gap: 'Z',

      PpTrHead: {
        extends: 'CaptionTitle',
        fontSize: 'Y2',
        Text: { text: '{{ workspace.people.rail.teams | polyglot }}' }
      },
      PpTrAll: {
        extends: 'PeopleFilterLink',
        state: { name: null, kind: 'group', label: 'workspace.people.filter.everyone' }
      },
      PpTrList: {
        flow: 'y',
        gap: 'Y',
        childExtends: 'PeopleFilterLink',
        childrenAs: 'state',
        children: (el) =>
          el
            .call('peopleTeamsDirectory')
            .filter((t) => el.call('isActiveTeamCategory', t.category))
            .map((t) => ({ name: t.name, label: t.name, count: t.count, kind: 'group' }))
      },

      PpTrInactiveHead: {
        hide: (el) =>
          !el
            .call('peopleTeamsDirectory')
            .some((t) => !el.call('isActiveTeamCategory', t.category)),
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'caption',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        paddingTop: 'A',
        text: '{{ workspace.people.rail.inactive | polyglot }}'
      },
      PpTrInactiveList: {
        flow: 'y',
        gap: 'Y',
        opacity: '0.75',
        childExtends: 'PeopleFilterLink',
        childrenAs: 'state',
        children: (el) =>
          el
            .call('peopleTeamsDirectory')
            .filter((t) => !el.call('isActiveTeamCategory', t.category))
            .map((t) => ({ name: t.name, label: t.name, count: t.count, kind: 'group' }))
      }
    },

    // Management lens (Everyone / Active / Inactive member counts). Status
    // derives from team membership. Its own AppSidebar card.
    PpMembersSummary: {
      flow: 'y',
      gap: 'Z',

      PpMsHead: {
        extends: 'CaptionTitle',
        fontSize: 'Y2',
        Text: { text: '{{ workspace.people.rail.members | polyglot }}' }
      },
      PpMsList: {
        flow: 'y',
        gap: 'Y',
        childExtends: 'PeopleFilterLink',
        childrenAs: 'state',
        children: (el) => el.call('peopleMemberStatusSummary')
      }
    }
  },

  AppMain: {
    Header: {
      extends: 'PageHeader',
      // One title per host (design-direction.md §3): under a pane title bar
      // the eyebrow + title pair repeats the bar word for word, so both yield.
      // The org-chart action stays. At the dedicated URL nothing changes.
      LeftGroup: {
        PpEyebrow: {
          hide: (el, s) => el.call('paneTitleBarShown', s),
          tag: 'span',
          fontSize: 'Z2',
          fontWeight: '700',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          // §4.4 accent ink — brand blue measured 3.21:1 as small-text ink on
          // the light stage (AA walk 2026-08-08); accentInk holds ≥4.9:1.
          color: 'accentInk',
          text: '{{ workspace.people.eyebrow | polyglot }}'
        },
        PpTitle: {
          hide: (el, s) => el.call('paneTitleBarShown', s),
          tag: 'h1',
          fontSize: 'B',
          fontWeight: '700',
          color: 'title',
          letterSpacing: '-0.02em',
          margin: '0',
          text: '{{ workspace.people.title | polyglot }}'
        }
      },
      RightGroup: {
        flow: 'x',
        align: 'center right',
        gap: 'Z',
        OrgChartBtn: {
          ..._headerBtn,
          text: '{{ workspace.people.orgChart | polyglot }}',
          onClick: (e, el, s) => el.call('orgChartOpen', s)
        }
      }
    },

    Body: {
      padding: 'C',
      overflow: 'auto',
      flow: 'y',
      gap: 'C',

      // Outstanding WorkspaceInvitation rows (Simone AI-assistant invites +
      // manual admin invites) — self-hides for non-admins and when there's
      // nothing pending. See components/PersonCard.js PendingInvitesBlock.
      PpPendingInvites: {
        extends: 'PendingInvitesBlock'
      },

      // ── THE ROSTER'S FOUR STATES ─────────────────────────────────────
      // This page used to implement ONE of them: the grid, plus a message
      // gated on "a filter is active". So a cold load painted a blank body,
      // a failed fetch was pixel-identical to an empty roster, and a
      // genuinely empty workspace rendered nothing at all.
      //
      // Every block below asks ONE resolver — `peopleRosterView`
      // (functions/peopleGroups.js) — and compares with its own name, so the
      // five blocks are mutually exclusive by construction. Its loading and
      // error answers come straight from `peopleStatus`
      // (functions/homeLoadState.js), the same one Home's widgets ask, so
      // the two surfaces can never disagree about which state the roster is
      // in. `Array.isArray(root.people)` is NOT that resolver and never can
      // be: state-app.js seeds `people: []`, so it is true from the first
      // frame; the positive marker `peopleLoaded` and its failure key
      // `peopleError` are what every producer writes, and that exact false
      // zero already shipped once (FALSE-ZERO-HOME-NON-AGENT-KEYS-1).
      //
      // `hide:` on every one, never `if:` — each must react AFTER mount,
      // when the fetch lands. `if:` is for "zero elements is the goal".
      // Contract pinned in __tests__/peopleRosterStates.test.js.

      // Permission, not emptiness: a viewer whose org member set comes back
      // empty (the non-mgmt roster projection — functions/peopleGroups.js)
      // sees a roster of exactly themselves. Gated on the REASON the scoping
      // resolver reports, never on "the list happens to be length 1".
      PpReducedNotice: {
        hide: (el, s) =>
          el.call('peopleStatus', s.root) !== 'loaded' || !el.call('peopleRosterIsReduced'),
        tag: 'p',
        role: 'status',
        margin: '0',
        padding: 'Z A',
        round: 'radiusCard',
        background: 'overlay',
        fontSize: 'Z2',
        color: 'caption',
        text: '{{ workspace.people.state.reduced | polyglot }}'
      },

      PpLoading: {
        // The roster's own track, so the skeleton breaks to the same columns
        // as the list it stands in for.
        extends: 'PeopleGrid',
        hide: (el) => el.call('peopleRosterView') !== 'loading',
        role: 'status',
        'aria-busy': 'true',
        'aria-label': (el) => el.call('polyglot', 'workspace.people.state.loading'),
        SkeletonCard: {},
        SkeletonCard_1: {},
        SkeletonCard_2: {},
        SkeletonCard_3: {},
        SkeletonCard_4: {},
        SkeletonCard_5: {}
      },

      // The shared `ErrorState` owns the markup: the icon, the `role="alert"`
      // + `aria-live="assertive"` region, the title and the hint. This block
      // used to rebuild all of it by hand and lost the icon and the live
      // region on the way.
      PpError: {
        extends: 'ErrorState',
        hide: (el) => el.call('peopleRosterView') !== 'error',
        kind: 'serverError',
        heading: 'workspace.people.state.errorTitle',
        hintText: 'workspace.people.state.errorBody',
        PpErrRetry: {
          // The ONE recovery action of an error poster is the brand Button in
          // solid ink — the same as pages/announcements.js's retry and
          // EmptyState's action, on the Button's own control scale (Rule 68).
          // It used to spread the header button, whose `overlay` fill is
          // invisible on this surface (measured live: gray2 at 5% over gray2),
          // so the retry read as a bare line of text under the message.
          extends: 'Button',
          theme: 'primary',
          text: '{{ common.action.try_again | polyglot }}',
          // Clearing the failure returns the page to its LOADING state (the
          // producer leaves `peopleLoaded` false on a failed read), so the
          // skeleton is what a person sees while the retry runs.
          // `retryWorkspaceActivation` is the house re-loader — it resolves
          // the SDK itself and re-runs the same boot load that writes both
          // roster keys.
          onClick: (e, el, s) => {
            s.root.update({ peopleError: null })
            el.call('retryWorkspaceActivation')
          }
        }
      },

      PpGroups: {
        // A half-loaded roster must never render as a confident list, and a
        // list of zero groups is one of the two empty states below, not an
        // empty grid.
        hide: (el) => el.call('peopleRosterView') !== 'list',
        flow: 'y',
        gap: 'D',
        childExtends: 'PeopleGroupBlock',
        childrenAs: 'state',
        children: (el) => el.call('groupFilteredPeople')
      },

      // A loaded workspace with nobody in it. The page owned no copy for
      // this at all: `PpEmpty` returned `true` on "no filter", so the body
      // was empty under the header.
      PpEmptyFresh: {
        extends: 'EmptyState',
        hide: (el) => el.call('peopleRosterView') !== 'emptyFresh',
        icon: 'people',
        heading: 'workspace.people.state.emptyTitle',
        hintText: 'workspace.people.state.emptyBody'
      },

      PpEmpty: {
        // The FILTERED empty ONLY. Gated on `peopleLoaded` as well, or it
        // flashes "No members match this filter." over a roster nobody has
        // read yet whenever a filter survives into a cold load.
        hide: (el) => el.call('peopleRosterView') !== 'emptyFiltered',
        flow: 'y',
        align: 'center center',
        gap: 'A',
        padding: 'D',
        PpEmptyText: {
          tag: 'p',
          fontSize: 'A',
          color: 'caption',
          textAlign: 'center',
          text: (el, s) => {
            const q = (s.root.peopleQuery || '').trim()
            if (q) return el.call('polyglot', 'workspace.people.emptyQuery').replace('{query}', q)
            if (s.root.selectedGroup)
              return el
                .call('polyglot', 'workspace.people.emptyGroup')
                .replace('{group}', s.root.selectedGroup)
            return el.call('polyglot', 'workspace.people.emptyFilter')
          }
        }
      }
    }
  }
}
